
import { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { IoClose } from "react-icons/io5";
import { API_BASE_URL } from "../../../../config";

const PERMISSIONS = {
  Projects: [
    { key: "project.add", label: "Add Project" },
    { key: "project.edit", label: "Edit Project" },
    { key: "project.delete", label: "Delete Project" },
    { key: "project.upload", label: "Upload Projects" },
  ],
  Customers: [
    { key: "customer.add", label: "Add Customer" },
    { key: "customer.edit", label: "Edit Customer" },
    { key: "customer.delete", label: "Delete Customer" },
    { key: "customer.upload", label: "Upload Customers" },
  ],
  Visits: [
    { key: "visit.add", label: "Add Visit" },
    { key: "visit.edit", label: "Edit Visit" },
  ],
  Bills: [
    { key: "bill.add", label: "Add Bill" },
    { key: "bill.edit", label: "Edit Bill" },
    { key: "bill.delete", label: "Delete Bill" },
  ],
  FinalBills: [
    { key: "finalBill.add", label: "Add Final Bill" },
  ],
};

const EditSubAdminForm = ({ onClose, subAdminData }) => {
  const [adminName, setAdminName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("Active");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Convert database permissions format to form format
  const convertDbPermissionsToForm = (dbPermissions) => {
    const formPermissions = [];
    
    // Get all possible permission keys from PERMISSIONS object
    const allPermissionKeys = Object.values(PERMISSIONS).flat();
    
    allPermissionKeys.forEach(perm => {
      const [module, action] = perm.key.split('.');
      const value = dbPermissions?.[module]?.[action] || false;
      formPermissions.push({ key: perm.key, value });
    });
    
    return formPermissions;
  };

  // Convert form permissions format to database format
  const convertFormPermissionsToDb = (formPermissions) => {
    const dbPermissions = {
      project: { add: false, edit: false, delete: false, upload: false },
      customer: { add: false, edit: false, delete: false, upload: false },
      visit: { add: false, edit: false },
      bill: { add: false, edit: false, delete: false },
      finalBill: { add: false },
    };

    formPermissions.forEach(perm => {
      const [module, action] = perm.key.split('.');
      if (dbPermissions[module] && dbPermissions[module][action] !== undefined) {
        dbPermissions[module][action] = perm.value;
      }
    });

    return dbPermissions;
  };

  // Load sub-admin data on mount
  useEffect(() => {
    if (subAdminData) {
      // console.log("Loading subAdminData:", subAdminData);
      
      setAdminName(subAdminData.adminName || "");
      setEmail(subAdminData.email || "");
      setStatus(subAdminData.status || "Active");


      // console.log("sub admin permissions",subAdminData.permissions)
      // Convert database permissions to form format
      const formPermissions = convertDbPermissionsToForm(subAdminData.permissions);
      // console.log("Converted Form Permissions:", formPermissions);
      
      setSelectedPermissions(formPermissions);
      // console.log(formPermissions)
      setIsLoading(false);
    }
  }, [subAdminData]);

  // Toggle permission value true/false
  const handlePermissionToggle = (key) => {
    setSelectedPermissions((prev) => {
      return prev.map((p) =>
        p.key === key ? { ...p, value: !p.value } : p
      );
    });
  };

  // Check if permission is ticked
  const isChecked = (key) => {
    // console.log("selectedPermissions",selectedPermissions);
    const perm = selectedPermissions.find((p) => p.key === key);
    return perm ? perm.value : false;
  };

  // Submit updated sub-admin
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    const loadingToast = toast.loading("Updating sub-admin...");

    try {
      // Convert form permissions back to database format
      const dbPermissions = convertFormPermissionsToDb(selectedPermissions);
      // console.log("Sending DB Permissions:", dbPermissions);

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/edit-subadmin/${subAdminData._id}`,
        { 
          adminName, 
          password: password || undefined, // Don't send empty password
          status, 
          permissions: dbPermissions 
        },
        { headers: { token } }
      );

      toast.dismiss(loadingToast);

      if (response.data.success) {
        toast.success("Sub-admin updated successfully");
        onClose();
      } else {
        toast.error(response.data.message || "Failed to update sub-admin");
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Error updating sub-admin");
      // console.error(error);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
        <div className="w-[600px] rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
          <div className="text-center text-gray-800 dark:text-white">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="w-[600px] rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
            Edit Sub Admin
          </h2>
          <button
            className="rounded bg-red-700 px-2 py-1 text-white transition hover:bg-red-800"
            onClick={onClose}
          >
            <IoClose size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
              Admin Name
            </label>
            <input
              type="text"
              placeholder="Name"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              className="w-full rounded border px-3 py-2 text-sm outline-none dark:bg-navy-700 dark:text-white"
              required
            />
          </div>

          {/* Email (readonly) */}
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
              Email
            </label>
            <input
              type="email"
              value={email}
              disabled
              className="w-full rounded border px-3 py-2 text-sm bg-gray-100 text-gray-500 dark:bg-navy-600 dark:text-gray-400"
            />
          </div>

          {/* Password */}
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
              Password
            </label>
            <input
              type="password"
              placeholder="New Password (leave blank to keep current)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded border px-3 py-2 text-sm outline-none dark:bg-navy-700 dark:text-white"
            />
          </div>

          {/* Status */}
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded border px-3 py-2 text-sm outline-none dark:bg-navy-700 dark:text-white"
              required
            >
              <option value="Active">Active</option>
              <option value="Delete">Delete</option>
              <option value="Block">Block</option>
            </select>
          </div>

          {/* Permissions */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              Permissions
            </h3>
            {Object.entries(PERMISSIONS).map(([module, perms]) => (
              <div key={module} className="border rounded-lg p-3 dark:border-gray-600">
                <h4 className="font-medium mb-2 text-gray-700 dark:text-gray-300">{module}</h4>
                <div className="grid grid-cols-2 gap-2">
                  {perms.map((perm) => (
                    <label key={perm.key} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isChecked(perm.key)}
                        onChange={() => handlePermissionToggle(perm.key)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          
          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-white dark:hover:bg-white/20"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSubAdminForm;

